import { createContext, useContext } from 'react';
import type { UnitShapeEditState, Piece } from 'components/geometry-editing';

/**
 * What the three regions share.
 *
 * The map, the gun barrel and the slots table all act on the same unit and the
 * same slot selection, so that state is held here rather than prop-drilled
 * through the shell — a region can be swapped out without the shell changing
 * shape. Mirrors the state the mock keeps in `TypeCurveSurface`.
 */
export interface UnitBuilderContextValue {
  inventoryWorkspaceId: number;
  /** Null for a draft unit that has not been saved yet. */
  unitId: number | null;
  /** Display name of the scoped unit, for the header. */
  unitName: string | null;
  /** The scoped leaf's type curve header detail, which the gun barrel keys on. */
  typeCurveHeaderDetailsId: number | null;
  /** The workspace has been promoted: composites can't be merged or split. */
  promoted: boolean;
  /**
   * Slots scoped by clicking a gun-barrel marker or ticking a slots-table row.
   * Only these get drag-to-trim grips on the map, so a stray drag cannot
   * silently resize a slot the user is not working on.
   */
  scopedSlotKeys: Set<number>;
  /** Slots marked unvalued — rendered struck through in every region. */
  unvaluedSlotKeys: Set<string>;
  /**
   * The working unit boundary, owned here rather than by the map.
   *
   * `TypeCurveMap`'s `unitShapeEdit` prop exists for exactly this: its own
   * comment calls the prop "the real contract: a future Unit Builder shell owns
   * the working boundary and passes it in". Holding it at this level is what
   * lets the Survey Areas panel's Undo and Save act on the same working copy the
   * map is editing.
   */
  shapeEdit: UnitShapeEditState;
  /** Pre-edit ring, drawn as the map's yellow dashed reference outline. */
  originalRing: Piece | null;
  /**
   * The unit's own footprint — the mock's `baseRing`, and the base of the
   * boundary composition (`composeUnitBoundary`).
   *
   * Always the TRUE server polygon: the scoped unit's for a real unit, its source
   * unit's for a clone. Deliberately has no frame-rectangle fallback, unlike
   * `originalRing` — a brand-new unit has no footprint to compose from, and
   * unioning the frame rectangle in would blow the boundary out to it. Sourced
   * from the boundary fetch rather than from `shapeEdit`, so an edit can never
   * feed back into the base it is composed against.
   */
  baseRing: Piece | null;
  /**
   * The scoped unit's stored GIS acreage (the development area's own figure), or
   * null for a draft / before it loads. Preferred over any client measurement of
   * the boundary, which is only a display approximation and disagrees with the DB.
   */
  unitAcreage: number | null;
}

export const UnitBuilderContext = createContext<UnitBuilderContextValue | null>(
  null
);

/**
 * Reads the Unit Builder context.
 * @return {UnitBuilderContextValue} The shared state.
 * @throws When called outside the Unit Builder.
 */
export const useUnitBuilder = (): UnitBuilderContextValue => {
  const value = useContext(UnitBuilderContext);
  if (!value) {
    throw new Error('useUnitBuilder must be used inside UnitBuilderModal');
  }
  return value;
};
